import { useState, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { ConfirmDeleteDialog } from "./ui/confirm-dialog";
import { Plus, Pencil, Trash2, Info, Target, Lock, FileUp, BarChart3 } from "lucide-react";
import { accountApi } from "../lib/api";
import { AiChat } from "./AiChat";
import { EvaluationCriteriaImportModal } from "./EvaluationCriteriaImportModal";
import { useSidebandVisible } from "../hooks/useSidebandVisible";

type Criterion = {
  id: string;
  simulatorId: string;
  // Chantier 2 (2026-06-01) : Mon compte utilise désormais ce tableau (modèle
  // matrice) pour rattacher un critère à un ou plusieurs simulateurs, au lieu
  // du legacy `simulatorId` unique. Un critère créé en matrice peut avoir
  // simulatorId=null mais applicableSimulatorIds=[X] — il sera bien rattaché à X.
  applicableSimulatorIds: string[];
  key: string;
  labelFr: string;
  labelEn: string;
  descriptionFr?: string | null;
  descriptionEn?: string | null;
  type: "score" | "boolean";
  maxScore?: number | null;
  successThreshold?: number | null;
  successLabelFr?: string | null;
  successLabelEn?: string | null;
  failLabelFr?: string | null;
  failLabelEn?: string | null;
  isSystem: boolean;
  isCore: boolean;
  isActive: boolean;
  showInProgressBar?: boolean;
  // Référentiel System partagé : pour un critère System, l'activation est PAR COMPTE.
  // disabledForAccount = ce compte a désactivé ce critère System (isActive reste l'état global ADM).
  disabledForAccount?: boolean;
  orderIndex: number;
};

type Simulator = { id: string; name: string; isActive?: boolean; scoringCriteria?: unknown };

const BLUE = "#384872";
const GREEN_TEXT = "#1E7A3C";
const GREEN_BG = "#7CBF94";

export function EvaluationCriteriaTab({ accountId, userProfileType }: { accountId?: string; userProfileType?: string }) {
  const { t, i18n } = useTranslation();
  const sidebandVisible = useSidebandVisible();
  const isFr = i18n.language === "fr";
  const isKestioAdmin = userProfileType === "ADM" || userProfileType === "COO";

  const [criteria, setCriteria] = useState<Criterion[]>([]);
  const [simulators, setSimulators] = useState<Simulator[]>([]);
  const [selectedSimulatorId, setSelectedSimulatorId] = useState<string>(""); // "" = tous
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Criterion | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Criterion | null>(null);
  const [showAiChat, setShowAiChat] = useState(false);
  const [companyDescription, setCompanyDescription] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [aiChatSimulatorId, setAiChatSimulatorId] = useState<string>("");

  useEffect(() => {
    let active = true;
    Promise.all([
      accountApi.getEvaluationCriteria({}, accountId),
      fetch("/api/agents", { credentials: "same-origin" }).then((r) => r.json()).catch(() => ({ agents: [] })),
      accountApi.getCompany(accountId).catch(() => null),
    ])
      .then(([crits, agentsJson, company]: [any[], any, any]) => {
        if (!active) return;
        setCriteria(crits || []);
        const agents: Simulator[] = Array.isArray(agentsJson) ? agentsJson : agentsJson.agents || agentsJson.simulators || [];
        const simsWithCriteria = agents.filter((s) => s.isActive !== false && s.scoringCriteria);
        setSimulators(simsWithCriteria);
        setCompanyDescription(company?.description || "");
      })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [accountId]);

  const save = async (data: Partial<Criterion>) => {
    try {
      if (editing?.id) {
        const updated = await accountApi.updateEvaluationCriterion(editing.id, data, accountId);
        setCriteria((prev) => prev.map((c) => (c.id === editing.id ? updated : c)));
      } else {
        if (!data.simulatorId) {
          alert("Veuillez sélectionner un simulateur.");
          return;
        }
        const created = await accountApi.createEvaluationCriterion({
          simulatorId: data.simulatorId,
          labelFr: data.labelFr || "",
          labelEn: data.labelEn || "",
          descriptionFr: data.descriptionFr || undefined,
          descriptionEn: data.descriptionEn || undefined,
          type: data.type || "score",
          maxScore: data.maxScore ?? undefined,
          successThreshold: data.successThreshold ?? undefined,
          successLabelFr: data.successLabelFr || undefined,
          successLabelEn: data.successLabelEn || undefined,
          failLabelFr: data.failLabelFr || undefined,
          failLabelEn: data.failLabelEn || undefined,
        }, accountId);
        setCriteria((prev) => [...prev, created]);
      }
      setEditing(null);
      setCreating(false);
    } catch (err: any) {
      alert(err.message || t("evaluationCriteria.errorSaving"));
    }
  };

  const remove = async (c: Criterion) => {
    try {
      await accountApi.deleteEvaluationCriterion(c.id, accountId);
      setCriteria((prev) => prev.filter((x) => x.id !== c.id));
      setConfirmDelete(null);
    } catch (err: any) {
      alert(err.message || t("evaluationCriteria.errorDeleting"));
    }
  };

  // État « activé pour ce compte » : critère System → dépend de disabledForAccount ; privé → isActive.
  const enabledForAccount = (c: Criterion) => (c.isSystem ? !c.disabledForAccount : c.isActive);

  const toggleActive = async (c: Criterion) => {
    if (c.isCore) return; // garde-fou UI (API refuserait aussi)
    try {
      const updated = await accountApi.toggleEvaluationCriterion(c.id, !enabledForAccount(c), accountId);
      setCriteria((prev) => prev.map((x) => (x.id === c.id ? updated : x)));
    } catch (err: any) {
      alert(err.message || t("evaluationCriteria.errorToggling"));
    }
  };

  const toggleProgressBar = async (c: Criterion) => {
    // Autorisé sur tous les critères (même System / Core) — voir handler API.
    try {
      const nextValue = c.showInProgressBar === false ? true : false;
      const updated = await accountApi.toggleEvaluationCriterionProgressBar(c.id, nextValue, accountId);
      setCriteria((prev) => prev.map((x) => (x.id === c.id ? updated : x)));
    } catch (err: any) {
      alert(err.message || t("evaluationCriteria.errorToggling"));
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground text-center py-8">{t("common.loading")}</p>;

  const simulatorsById = new Map(simulators.map((s) => [s.id, s]));

  // Chantier 2 (2026-06-01) : filtrage par applicableSimulatorIds[] (modèle
  // matrice) au lieu du legacy simulatorId. Tri par le 1er simulateur applicable
  // (pour grouper visuellement) puis par orderIndex.
  const visibleCriteria = (selectedSimulatorId
    ? criteria.filter((c) => (c.applicableSimulatorIds ?? []).includes(selectedSimulatorId))
    : criteria
  ).slice().sort((a, b) => {
    const aFirstSim = (a.applicableSimulatorIds ?? [])[0] || "";
    const bFirstSim = (b.applicableSimulatorIds ?? [])[0] || "";
    const aName = simulatorsById.get(aFirstSim)?.name || "";
    const bName = simulatorsById.get(bFirstSim)?.name || "";
    const cmpSim = aName.localeCompare(bName);
    if (cmpSim !== 0) return cmpSim;
    return a.orderIndex - b.orderIndex;
  });

  // AiChat ouvert ?
  const aiChatSim = simulators.find((s) => s.id === aiChatSimulatorId);
  if (showAiChat && aiChatSim) {
    const existingList = criteria
      .filter((c) => (c.applicableSimulatorIds ?? []).includes(aiChatSim.id))
      .map((c) => `- ${c.labelFr} / ${c.labelEn} (${c.type}${c.isCore ? ", structurel" : ""})`)
      .join("\n");
    return (
      <div className="space-y-3">
        <Button variant="outline" size="sm" onClick={() => { setShowAiChat(false); setAiChatSimulatorId(""); }}>
          {t("evaluationCriteria.backToGrid")}
        </Button>
        <div style={{ height: "70vh" }}>
          <AiChat
            agentCode="criteria_creator"
            robotColor="#988EF2"
            bgColor="#f8f5ff"
            context={{
              companyDescription,
              simulatorName: aiChatSim.name,
              simulatorDescription: (aiChatSim as any).description || "",
              existingCriteria: existingList || "(aucun critère existant)",
              language: i18n.language,
            }}
            onItemCreated={async (_type, data) => {
              try {
                const created = await accountApi.createEvaluationCriterion({
                  simulatorId: aiChatSim.id,
                  labelFr: data.labelFr || data.labelEn || "",
                  labelEn: data.labelEn || data.labelFr || "",
                  descriptionFr: data.descriptionFr,
                  descriptionEn: data.descriptionEn,
                  type: data.type || "score",
                  maxScore: data.maxScore,
                  successThreshold: data.successThreshold,
                  successLabelFr: data.successLabelFr,
                  successLabelEn: data.successLabelEn,
                  failLabelFr: data.failLabelFr,
                  failLabelEn: data.failLabelEn,
                }, accountId);
                setCriteria((prev) => [...prev, created]);
              } catch (err: any) {
                alert(err.message || t("evaluationCriteria.errorCreating"));
              }
            }}
            onComplete={() => { setShowAiChat(false); setAiChatSimulatorId(""); }}
            title={t("evaluationCriteria.aiTitle", { simulator: aiChatSim.name })}
            subtitle={t("evaluationCriteria.aiSubtitle")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Bandeau explicatif */}
      <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-[#384872]">
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium mb-1">{t("evaluationCriteria.infoTitle")}</p>
            <p><Trans i18nKey="evaluationCriteria.infoLine1" components={{ 1: <strong />, 2: <strong /> }} /></p>
            <p><Trans i18nKey="evaluationCriteria.infoLine2" components={{ 1: <Lock className="inline h-3 w-3 mx-0.5" />, 2: <strong /> }} /></p>
            <p className="mt-1"><Trans i18nKey="evaluationCriteria.infoLine3" components={{ 1: <strong /> }} /></p>
          </div>
        </div>
      </div>

      {/* Barre d'actions : filtre + boutons création */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end sm:justify-between">
        <div className="flex-1 max-w-xs">
          <Label className="text-xs text-muted-foreground">{t("evaluationCriteria.filterBySimulator")}</Label>
          <Select value={selectedSimulatorId || "__all__"} onValueChange={(v) => setSelectedSimulatorId(v === "__all__" ? "" : v)}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{t("evaluationCriteria.allSimulators")}</SelectItem>
              {simulators.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {!creating && !editing && (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-[#988EF2] text-[#5A4BBE] hover:bg-[#988EF2]/10"
              onClick={() => setShowImport(true)}
            >
              <FileUp className="h-4 w-4 mr-2" /> {t("evaluationCriteria.importDocument")}
            </Button>
            {/* Créer avec l'IA : nécessite un simulateur cible */}
            <AiCreateButton
              simulators={simulators}
              selectedSimulatorId={selectedSimulatorId}
              onStart={(simId) => { setAiChatSimulatorId(simId); setShowAiChat(true); }}
            />
            {/* Création manuelle : ADM/COO uniquement (évite critères mal catégorisés côté client) */}
            {isKestioAdmin && (
              <Button
                size="sm"
                className="bg-[#384872] hover:bg-[#2d3a5c]"
                onClick={() => { setCreating(true); setEditing(null); }}
              >
                <Plus className="h-4 w-4 mr-2" /> {t("evaluationCriteria.newCriterion")}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Form création (ADM/COO) */}
      {creating && isKestioAdmin && (
        <CriterionForm
          simulatorName=""
          simulators={simulators}
          defaultSimulatorId={selectedSimulatorId || simulators[0]?.id}
          criterion={null}
          isFr={isFr}
          onSave={save}
          onCancel={() => { setCreating(false); setEditing(null); }}
        />
      )}

      {/* Form édition */}
      {editing && !creating && (
        <CriterionForm
          simulatorName={simulatorsById.get(editing.simulatorId)?.name || ""}
          simulators={simulators}
          defaultSimulatorId={editing.simulatorId}
          criterion={editing}
          isFr={isFr}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      )}

      {/* Liste compacte : nom — description — type — simulateur — System/Interne */}
      {visibleCriteria.length === 0 && !creating ? (
        <div className="text-center py-12">
          <Target className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground">{t("evaluationCriteria.noCriteria")}</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {visibleCriteria.map((c) => {
            // Chantier 2.1 (2026-06-02) : résolution du nom de simulateur via
            // applicableSimulatorIds[] (modèle matrice) avec fallback sur le
            // legacy c.simulatorId. Les critères créés depuis la matrice ont
            // simulatorId = NULL, seul applicableSimulatorIds[] est rempli.
            // Si l'utilisateur a filtré sur un simulateur précis, on affiche
            // ce simulateur (puisque c'est celui qu'il consulte) — pas un
            // « +N » qui prêterait à confusion sur le nombre d'autres rattachés.
            const simIds = (c.applicableSimulatorIds && c.applicableSimulatorIds.length > 0)
              ? c.applicableSimulatorIds
              : (c.simulatorId ? [c.simulatorId] : []);
            const displaySimId = (selectedSimulatorId && simIds.includes(selectedSimulatorId))
              ? selectedSimulatorId
              : simIds[0];
            const simName = displaySimId ? (simulatorsById.get(displaySimId)?.name || "?") : "?";
            const desc = isFr ? (c.descriptionFr || c.descriptionEn) : (c.descriptionEn || c.descriptionFr);
            return (
              <Card key={c.id} className={!enabledForAccount(c) ? "opacity-60" : ""}>
                <CardContent className="!py-2 !px-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0 flex items-center gap-3 flex-wrap">
                      {/* 1. Nom */}
                      <span className="font-medium text-sm whitespace-nowrap" style={{ color: BLUE }}>
                        {isFr ? (c.labelFr || c.labelEn) : (c.labelEn || c.labelFr)}
                      </span>
                      {/* 2. Description (complète, wrap multi-lignes) */}
                      {desc && (
                        <span className="text-xs text-muted-foreground basis-full whitespace-normal break-words">
                          — {desc}
                        </span>
                      )}
                      {/* 3. Type de scoring */}
                      <Badge variant="outline" className="text-xs shrink-0">
                        {c.type === "score" ? t("evaluationCriteria.scoreLabel", { max: c.maxScore || 10 }) : t("evaluationCriteria.yesNoLabel")}
                      </Badge>
                      {/* 4. Simulateur associé */}
                      <Badge variant="outline" className="text-xs shrink-0 border-slate-300 text-slate-600">
                        {simName}
                      </Badge>
                      {/* 5. System/Interne */}
                      {c.isSystem ? (
                        <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200 text-xs shrink-0">{t("evaluationCriteria.badgeSystem")}</Badge>
                      ) : (
                        <Badge className="text-xs shrink-0" style={{ background: GREEN_BG, color: GREEN_TEXT }}>{t("evaluationCriteria.badgeInterne")}</Badge>
                      )}
                      {c.isCore && (
                        <span
                          className="inline-flex items-center gap-0.5 text-xs text-slate-500 shrink-0"
                          title={t("evaluationCriteria.coreTooltip")}
                        >
                          <Lock className="h-3 w-3" />
                        </span>
                      )}
                      {/* 6. Suivi live (tag cliquable — toggle showInProgressBar).
                             Présent sur TOUS les critères (score + booléen) : le flag
                             sert aussi au workflow N8N chuchoteur pour filtrer les
                             critères qu'il reçoit. Sur un critère booléen (objectif
                             final), le tag n'a pas d'effet visuel côté LiveFeedbackPanel
                             (les booléens ne s'affichent jamais en barre), mais il
                             contrôle bien ce qui est envoyé au chuchoteur. */}
                      {/* Interrupteur du feedback en direct fermé (`sideband-visibility.ts`) : pas de bouton « Barre ». */}
                      {sidebandVisible && (
                      <button
                          type="button"
                          onClick={() => toggleProgressBar(c)}
                          title={
                            c.showInProgressBar === false
                              ? t("evaluationCriteria.progressBarBadgeShow")
                              : t("evaluationCriteria.progressBarBadgeHide")
                          }
                          aria-label={
                            c.showInProgressBar === false
                              ? t("evaluationCriteria.progressBarBadgeShow")
                              : t("evaluationCriteria.progressBarBadgeHide")
                          }
                          aria-pressed={c.showInProgressBar !== false}
                          className={
                            c.showInProgressBar === false
                              ? "inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-400 line-through hover:bg-slate-100 transition-colors shrink-0"
                              : "inline-flex items-center gap-1 rounded-md border border-transparent px-2 py-0.5 text-xs font-medium hover:opacity-80 transition-opacity shrink-0"
                          }
                          style={
                            c.showInProgressBar === false
                              ? undefined
                              : { background: GREEN_BG, color: GREEN_TEXT }
                          }
                        >
                          <BarChart3 className="h-3 w-3" />
                          {t("evaluationCriteria.progressBarBadge")}
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Switch
                        checked={enabledForAccount(c)}
                        onCheckedChange={() => toggleActive(c)}
                        disabled={c.isCore}
                        aria-label={enabledForAccount(c) ? t("evaluationCriteria.ariaDeactivate") : t("evaluationCriteria.ariaActivate")}
                      />
                      {!c.isSystem && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            aria-label={t("evaluationCriteria.ariaEdit")}
                            onClick={() => { setEditing(c); setCreating(false); }}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-red-500"
                            aria-label={t("evaluationCriteria.ariaDelete")}
                            onClick={() => setConfirmDelete(c)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {confirmDelete && (
        <ConfirmDeleteDialog
          open={!!confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          itemName={(isFr ? confirmDelete.labelFr : confirmDelete.labelEn) || confirmDelete.key}
          onConfirm={() => remove(confirmDelete)}
        />
      )}

      <EvaluationCriteriaImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        accountId={accountId}
        simulators={simulators}
        onCreated={async (count) => {
          if (count > 0) {
            // Reload grille du compte après bulk create
            try {
              const refreshed = await accountApi.getEvaluationCriteria({}, accountId);
              setCriteria(refreshed || []);
            } catch {}
          }
        }}
      />
    </div>
  );
}

function AiCreateButton({
  simulators,
  selectedSimulatorId,
  onStart,
}: {
  simulators: Simulator[];
  selectedSimulatorId: string;
  onStart: (simulatorId: string) => void;
}) {
  const { t } = useTranslation();
  const [picking, setPicking] = useState(false);
  const [pickedId, setPickedId] = useState("");

  const handleClick = () => {
    if (selectedSimulatorId) {
      onStart(selectedSimulatorId);
    } else {
      setPicking(true);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="border-[#988EF2] text-[#5A4BBE] hover:bg-[#988EF2]/10"
        onClick={handleClick}
      >
        {t("evaluationCriteria.aiCreate")}
      </Button>
      <Dialog open={picking} onOpenChange={(o) => !o && setPicking(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("evaluationCriteria.pickSimulatorTitle")}</DialogTitle>
            <DialogDescription>
              {t("evaluationCriteria.pickSimulatorDescription")}
            </DialogDescription>
          </DialogHeader>
          <Select value={pickedId} onValueChange={setPickedId}>
            <SelectTrigger>
              <SelectValue placeholder={t("evaluationCriteria.pickSimulatorPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {simulators.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setPicking(false)}>{t("common.cancel")}</Button>
            <Button
              size="sm"
              className="bg-[#384872] hover:bg-[#2d3a5c]"
              disabled={!pickedId}
              onClick={() => { onStart(pickedId); setPicking(false); setPickedId(""); }}
            >
              {t("evaluationCriteria.startAssistant")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CriterionForm({
  simulatorName,
  simulators,
  defaultSimulatorId,
  criterion,
  isFr,
  onSave,
  onCancel,
}: {
  simulatorName: string;
  simulators: Simulator[];
  defaultSimulatorId?: string;
  criterion: Criterion | null;
  isFr: boolean;
  onSave: (data: Partial<Criterion>) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<Partial<Criterion>>(() => ({
    simulatorId: criterion?.simulatorId || defaultSimulatorId || "",
    labelFr: criterion?.labelFr || "",
    labelEn: criterion?.labelEn || "",
    descriptionFr: criterion?.descriptionFr || "",
    descriptionEn: criterion?.descriptionEn || "",
    type: criterion?.type || "score",
    maxScore: criterion?.maxScore ?? 10,
    successThreshold: criterion?.successThreshold ?? undefined,
    successLabelFr: criterion?.successLabelFr || "",
    successLabelEn: criterion?.successLabelEn || "",
    failLabelFr: criterion?.failLabelFr || "",
    failLabelEn: criterion?.failLabelEn || "",
  }));

  const isEdit = !!criterion?.id;
  const primaryLang = isFr ? "fr" : "en";
  const secondaryLang = isFr ? "en" : "fr";
  const [translating, setTranslating] = useState(false);

  const handleTranslate = async () => {
    // Traduit depuis la langue courante (remplie) vers la langue secondaire
    const fromLabel = primaryLang === "fr" ? form.labelFr : form.labelEn;
    const fromDesc = primaryLang === "fr" ? form.descriptionFr : form.descriptionEn;
    const fromSuccess = primaryLang === "fr" ? form.successLabelFr : form.successLabelEn;
    const fromFail = primaryLang === "fr" ? form.failLabelFr : form.failLabelEn;

    if (!fromLabel?.trim() && !fromDesc?.trim()) {
      alert(t("evaluationCriteria.alertSetLabelBeforeTranslate", { lang: primaryLang.toUpperCase() }));
      return;
    }

    setTranslating(true);
    try {
      const updates: Partial<Criterion> = { ...form };
      if (fromLabel?.trim()) {
        const translated = await accountApi.translateText(fromLabel, secondaryLang as "fr" | "en", "critère d'évaluation commercial — label court");
        if (secondaryLang === "fr") updates.labelFr = translated;
        else updates.labelEn = translated;
      }
      if (fromDesc?.trim()) {
        const translated = await accountApi.translateText(fromDesc, secondaryLang as "fr" | "en", "description d'un critère d'évaluation pour IA de feedback");
        if (secondaryLang === "fr") updates.descriptionFr = translated;
        else updates.descriptionEn = translated;
      }
      if (form.type === "boolean") {
        if (fromSuccess?.trim()) {
          const translated = await accountApi.translateText(fromSuccess, secondaryLang as "fr" | "en", "label court pour un résultat 'Oui/atteint'");
          if (secondaryLang === "fr") updates.successLabelFr = translated;
          else updates.successLabelEn = translated;
        }
        if (fromFail?.trim()) {
          const translated = await accountApi.translateText(fromFail, secondaryLang as "fr" | "en", "label court pour un résultat 'Non/non atteint'");
          if (secondaryLang === "fr") updates.failLabelFr = translated;
          else updates.failLabelEn = translated;
        }
      }
      setForm(updates);
    } catch (err: any) {
      alert(err.message || t("evaluationCriteria.errorTranslating"));
    } finally {
      setTranslating(false);
    }
  };

  const canSave = (!!form.labelFr?.trim() || !!form.labelEn?.trim()) && (isEdit || !!form.simulatorId);

  return (
    <Card className="border-2 border-blue-200">
      <CardContent className="!py-4 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium" style={{ color: BLUE }}>
            {isEdit ? t("evaluationCriteria.editTitle") : t("evaluationCriteria.createTitle")}{simulatorName ? <> — <span className="text-muted-foreground">{simulatorName}</span></> : null}
          </p>
          <Button variant="outline" size="sm" onClick={handleTranslate} type="button" disabled={translating}>
            {translating ? t("evaluationCriteria.translating") : t("evaluationCriteria.translateButton")}
          </Button>
        </div>

        {/* Simulateur (création uniquement, édition : read-only) */}
        {!isEdit && (
          <div className="space-y-1">
            <Label>{t("evaluationCriteria.simulatorLabel")}</Label>
            <Select value={form.simulatorId || ""} onValueChange={(v) => setForm({ ...form, simulatorId: v })}>
              <SelectTrigger>
                <SelectValue placeholder={t("evaluationCriteria.pickSimulatorPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {simulators.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Type */}
        <div className="space-y-1">
          <Label>{t("evaluationCriteria.typeLabel")}</Label>
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v as "score" | "boolean" })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="score">{t("evaluationCriteria.typeScore")}</SelectItem>
              <SelectItem value="boolean">{t("evaluationCriteria.typeBoolean")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Labels — langue courante en haut */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>{t("evaluationCriteria.labelFieldRequired", { lang: primaryLang.toUpperCase() })}</Label>
            <Input
              value={(primaryLang === "fr" ? form.labelFr : form.labelEn) || ""}
              onChange={(e) =>
                setForm({ ...form, [primaryLang === "fr" ? "labelFr" : "labelEn"]: e.target.value })
              }
              placeholder={primaryLang === "fr" ? t("evaluationCriteria.labelPlaceholderFr") : t("evaluationCriteria.labelPlaceholderEn")}
            />
          </div>
          <div className="space-y-1">
            <Label>{t("evaluationCriteria.labelFieldOptional", { lang: secondaryLang.toUpperCase() })}</Label>
            <Input
              value={(secondaryLang === "fr" ? form.labelFr : form.labelEn) || ""}
              onChange={(e) =>
                setForm({ ...form, [secondaryLang === "fr" ? "labelFr" : "labelEn"]: e.target.value })
              }
              placeholder={secondaryLang === "en" ? t("evaluationCriteria.labelPlaceholderEn") : t("evaluationCriteria.labelPlaceholderFr")}
            />
          </div>
        </div>

        {/* Descriptions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>{t("evaluationCriteria.descriptionField", { lang: primaryLang.toUpperCase() })}</Label>
            <textarea
              className="w-full min-h-[80px] px-3 py-2 rounded-md border border-input bg-background text-sm resize-y"
              value={(primaryLang === "fr" ? form.descriptionFr : form.descriptionEn) || ""}
              onChange={(e) =>
                setForm({ ...form, [primaryLang === "fr" ? "descriptionFr" : "descriptionEn"]: e.target.value })
              }
              placeholder={primaryLang === "fr" ? t("evaluationCriteria.descriptionPlaceholderFr") : t("evaluationCriteria.descriptionPlaceholderEn")}
            />
          </div>
          <div className="space-y-1">
            <Label>{t("evaluationCriteria.descriptionField", { lang: secondaryLang.toUpperCase() })}</Label>
            <textarea
              className="w-full min-h-[80px] px-3 py-2 rounded-md border border-input bg-background text-sm resize-y"
              value={(secondaryLang === "fr" ? form.descriptionFr : form.descriptionEn) || ""}
              onChange={(e) =>
                setForm({ ...form, [secondaryLang === "fr" ? "descriptionFr" : "descriptionEn"]: e.target.value })
              }
              placeholder={secondaryLang === "en" ? t("evaluationCriteria.descriptionFallbackEn") : t("evaluationCriteria.descriptionFallbackFr")}
            />
          </div>
        </div>

        {/* Champs conditionnels au type */}
        {form.type === "score" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>{t("evaluationCriteria.maxScoreLabel")}</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={form.maxScore ?? 10}
                onChange={(e) => setForm({ ...form, maxScore: parseInt(e.target.value) || 10 })}
              />
            </div>
            <div className="space-y-1">
              <Label>{t("evaluationCriteria.successThresholdLabel")}</Label>
              <Input
                type="number"
                min={0}
                value={form.successThreshold ?? ""}
                onChange={(e) =>
                  setForm({ ...form, successThreshold: e.target.value ? parseInt(e.target.value) : undefined })
                }
                placeholder={t("evaluationCriteria.successThresholdPlaceholder")}
              />
            </div>
          </div>
        )}

        {form.type === "boolean" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>{t("evaluationCriteria.successFrLabel")}</Label>
              <Input
                value={form.successLabelFr || ""}
                onChange={(e) => setForm({ ...form, successLabelFr: e.target.value })}
                placeholder={t("evaluationCriteria.successFrPlaceholder")}
              />
            </div>
            <div className="space-y-1">
              <Label>{t("evaluationCriteria.successEnLabel")}</Label>
              <Input
                value={form.successLabelEn || ""}
                onChange={(e) => setForm({ ...form, successLabelEn: e.target.value })}
                placeholder={t("evaluationCriteria.successEnPlaceholder")}
              />
            </div>
            <div className="space-y-1">
              <Label>{t("evaluationCriteria.failFrLabel")}</Label>
              <Input
                value={form.failLabelFr || ""}
                onChange={(e) => setForm({ ...form, failLabelFr: e.target.value })}
                placeholder={t("evaluationCriteria.failFrPlaceholder")}
              />
            </div>
            <div className="space-y-1">
              <Label>{t("evaluationCriteria.failEnLabel")}</Label>
              <Input
                value={form.failLabelEn || ""}
                onChange={(e) => setForm({ ...form, failLabelEn: e.target.value })}
                placeholder={t("evaluationCriteria.failEnPlaceholder")}
              />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" size="sm" onClick={onCancel} type="button">{t("common.cancel")}</Button>
          <Button
            size="sm"
            disabled={!canSave}
            className="bg-[#384872] hover:bg-[#2d3a5c]"
            onClick={() => onSave(form)}
            type="button"
          >
            {isEdit ? t("common.save") : t("common.create")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
